// opervex-frontend/src/components/Financial/ClearingModal.js

import React, { useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { FaPlus, FaSearch, FaTrash } from "react-icons/fa";

import CurrencyInput from "../CurrencyInput";
import { SelectModal } from "../../utils/SelectModal";
import useOpervex from "../../hooks/useOpervex";

const ClearingModalComponent = ({ documentId, show, onHide, onConfirm, currency = "BRL", balanceAmount = 0 }) => {
  const api = useOpervex();

  const [documents, setDocuments] = useState([]);

  const [clearing, setClearing] = useState({
    clearingDate: new Date().toISOString().split("T")[0],

    interestAmount: 0,
    fineAmount: 0,
    discountAmount: 0,

    notes: "",
  });

  // Soma o saldo disponível dos documentos selecionados.
  const documentsAmount = useMemo(() => {
    return documents.reduce((total, document) => {
      const amount = Number(document.allocatedAmount ?? document.balance ?? document.totalAmount ?? 0);

      return total + amount;
    }, 0);
  }, [documents]);

  // Valores adicionais da baixa.
  const interestAmount = Number(clearing.interestAmount || 0);
  const fineAmount = Number(clearing.fineAmount || 0);
  const discountAmount = Number(clearing.discountAmount || 0);

  // Valor total considerando documentos + juros + multa - desconto.
  const calculatedTotalAmount = documentsAmount + interestAmount + fineAmount - discountAmount;

  // Impede que a baixa fique maior que o saldo do documento principal.
  const totalAmount = Math.min(calculatedTotalAmount, Number(balanceAmount || 0));

  // Saldo restante do documento principal.
  const balanceAfterClearing = Math.max(Number(balanceAmount || 0) - totalAmount, 0);

  const hasPartialDocument = documents.some(
    (document) => Number(document.allocatedAmount || 0) < Number(document.balance ?? document.totalAmount ?? 0),
  );

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency,
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setClearing((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSearchNewClearingDocument = async () => {
    const docs = await api.Financial.Entries.findAllModal([
      {
        field: "id",
        condition: "<>",
        value: documentId,
      },
      {
        field: "status",
        condition: "different",
        value: "Baixado",
      },
    ]);

    if (!docs) return null;

    docs.forEach((doc) => {
      doc.selected = documents.some((item) => String(item.id) === String(doc.id));
    });

    const selecteds = await SelectModal({
      title: "Seleção de documentos",
      multiple: true,

      fields: [
        {
          key: "id",
          label: "Código",
        },
        {
          key: "accountType",
          label: "Operação",
        },
        {
          key: "documentType",
          label: "Tipo Documento",
        },
        {
          key: "documentDate",
          label: "Documento",
        },
        {
          key: "dueDate",
          label: "Vencimento",
        },
        {
          key: "referenceKey",
          label: "Referência",
        },
        {
          key: "balance",
          label: "Saldo",
        },
      ],

      items: docs,
    });

    if (!selecteds) return null;

    return selecteds;
  };

  const handleSearchDocument = async () => {
    const selecteds = await handleSearchNewClearingDocument();

    if (!selecteds) return;

    setDocuments((current) => {
      const newDocuments = [...current];
      const additionalAmounts = interestAmount + fineAmount - discountAmount;
      let availableAmount = Math.max(
        Number(balanceAmount || 0) -
          newDocuments.reduce(
            (total, document) => total + Number(document.allocatedAmount ?? document.balance ?? document.totalAmount ?? 0),
            0,
          ) -
          additionalAmounts,
        0,
      );

      selecteds.forEach((row) => {
        const alreadyExists = newDocuments.some((item) => String(item.id) === String(row.id));
        const documentBalance = Number(row.balance ?? row.totalAmount ?? 0);

        if (!alreadyExists && availableAmount > 0 && documentBalance > 0) {
          const allocatedAmount = Math.min(documentBalance, availableAmount);

          newDocuments.push({ ...row, allocatedAmount });
          availableAmount -= allocatedAmount;
        }
      });

      return newDocuments;
    });
  };

  const handleRemoveDocument = (id) => {
    setDocuments((current) => current.filter((item) => String(item.id) !== String(id)));
  };

  const handleConfirm = () => {
    if (documents.length === 0) {
      return;
    }

    onConfirm({
      ...clearing,

      amount: documentsAmount,

      interestAmount,
      fineAmount,
      discountAmount,

      totalAmount,

      documents,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Nova Baixa / Compensação</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="g-2 mb-3">
          <Col>
            <Button variant="primary" size="sm" onClick={handleSearchDocument}>
              <FaSearch /> Selecionar documento
            </Button>
          </Col>
        </Row>

        {documents.length > 0 && (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Data</th>
                <th>Valor Documento</th>
                <th>Saldo</th>
                <th>Valor compensado</th>

                <th
                  style={{
                    width: "70px",
                  }}
                >
                  Ação
                </th>
              </tr>
            </thead>

            <tbody>
              {documents.map((document) => {
                const documentTotal = Number(document.totalAmount || 0);

                const documentBalance = Number(document.balance ?? document.totalAmount ?? 0);
                const allocatedAmount = Number(document.allocatedAmount ?? documentBalance);

                return (
                  <tr key={document.id}>
                    <td>{document.id}</td>
                    <td>{document.documentDate || "-"}</td>
                    <td className="text-end">{formatCurrency(documentTotal)}</td>
                    <td className="text-end">{formatCurrency(documentBalance)}</td>
                    <td className="text-end fw-bold">{formatCurrency(allocatedAmount)}</td>
                    <td className="text-center">
                      <Button variant="danger" size="sm" onClick={() => handleRemoveDocument(document.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={4} className="text-end fw-bold">
                  Total selecionado
                </td>

                <td className="text-end fw-bold">{formatCurrency(documentsAmount)}</td>

                <td />
              </tr>
            </tfoot>
          </Table>
        )}

        <Row className="g-2 mb-2">
          <Col md={3}>
            <Form.Label>Valor Documentos</Form.Label>

            <Form.Control size="sm" value={formatCurrency(documentsAmount)} disabled />
          </Col>

          <Col md={3}>
            <Form.Label>Juros</Form.Label>

            <CurrencyInput name="interestAmount" value={clearing.interestAmount || 0} onChange={handleChange} currency={currency} />
          </Col>

          <Col md={3}>
            <Form.Label>Multa</Form.Label>

            <CurrencyInput name="fineAmount" value={clearing.fineAmount || 0} onChange={handleChange} currency={currency} />
          </Col>

          <Col md={3}>
            <Form.Label>Desconto</Form.Label>

            <CurrencyInput name="discountAmount" value={clearing.discountAmount || 0} onChange={handleChange} currency={currency} />
          </Col>
        </Row>

        <Row className="g-2">
          <Col md={3}>
            <Form.Label>Total da Baixa</Form.Label>

            <Form.Control size="sm" value={formatCurrency(totalAmount)} disabled />
          </Col>

          <Col md={3}>
            <Form.Label>Saldo após Baixa</Form.Label>

            <Form.Control size="sm" value={formatCurrency(balanceAfterClearing)} disabled />
          </Col>

          <Col md={6}>
            <Form.Label>Motivo / Observação</Form.Label>

            <Form.Control size="sm" name="notes" value={clearing.notes || ""} onChange={handleChange} />
          </Col>
        </Row>

        {hasPartialDocument && (
          <div className="alert alert-warning mt-3 mb-0 py-2">
            O valor dos documentos selecionados é maior que o saldo do lançamento. A baixa será limitada a{" "}
            <strong>{formatCurrency(balanceAmount)}</strong>.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          Cancelar
        </Button>

        <Button variant="success" size="sm" onClick={handleConfirm} disabled={documents.length === 0 || totalAmount <= 0}>
          <FaPlus /> Confirmar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClearingModalComponent;
